/**
 * Zod contracts for the two documents the Total Station JSON upload accepts.
 *
 * Both are free-form records: the document keys are operator-chosen (anchor
 * serial numbers, pitch point labels), and every value must match the entry
 * shape. Unrecognised keys inside an entry are permitted.
 *
 * The schemas validate but never produce the stored value — `z.object` strips
 * unrecognised keys, so callers keep the `JSON.parse` result and use
 * `safeParse` only for the verdict and its issue list.
 *
 * These upload types are deliberately distinct from `Anchor`, `Anchors`,
 * `Coordinate` and `PitchCoordinates` in `./types`: the upload contract makes
 * `serial` optional, omits `encodedId`, and requires `z`.
 */
import { z } from 'zod'

const MAX_UINT32 = 4294967295

/**
 * Crockford base32: digits plus letters excluding I, L, O and U in either
 * case, and the grouping hyphen. Total-station exports emit the anchor's
 * printed serial (`CB80MR`) here.
 */
const CROCKFORD_CODE_PATTERN = /^[0-9A-HJKMNP-TV-Za-hjkmnp-tv-z-]+$/

/**
 * A code the API can decode to a `uint32`. It tries a base32 decode first and
 * falls back to parsing the text as a plain integer, so a long digit string
 * like `414450445` is valid even though it overflows as base32 — the e2e
 * fixtures key their anchors exactly that way.
 *
 * Only the character set is checked. Length is deliberately not: every string
 * the integer fallback accepts is also within this character set, so the set
 * alone cannot reject anything the API would take. A code that overflows both
 * paths (`ZZZZZZZZ`) still reaches the API and 400s there — accepted, because
 * the alternative is a length rule that rejects valid files.
 */
export const crockfordCodeSchema = z
  .string()
  .min(1, 'must not be empty')
  .regex(
    CROCKFORD_CODE_PATTERN,
    'must be a Crockford code — digits and letters, excluding I, L, O and U'
  )

/**
 * The API decodes this field into a `uint32` anchor id, accepting either a
 * Crockford code or the already-decoded number — it tries the base32 decode
 * first and falls back to a plain integer. Both forms reach production, so
 * both are accepted here.
 */
export const anchorIdSchema = z.union(
  [
    z
      .number()
      .int('numeric id must be a whole number')
      .min(0, 'numeric id must not be negative')
      .max(MAX_UINT32, `numeric id must be at most ${MAX_UINT32}`),
    crockfordCodeSchema
  ],
  {
    /**
     * A failed union reports a single `invalid_union` issue and discards the
     * branch messages, so an absent or wrongly-typed id — the likeliest real
     * failure — would otherwise read only "Invalid input". Name both accepted
     * forms for that code and leave every other issue with its own message.
     */
    error: (issue) =>
      issue.code === 'invalid_union'
        ? `must be a whole number from 0 to ${MAX_UINT32}, or a Crockford code`
        : undefined
  }
)

export const pitchPointUploadEntrySchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number()
})

/**
 * `index` and `serial` are optional because the API defaults them when
 * absent — `index` is flagged for removal there, and Gen 1 anchors carry no
 * serial. `id` and `pos` stay required: the API would silently substitute a
 * zeroed anchor at the origin, which is the corruption this validation
 * exists to catch.
 *
 * Gen 2 (angle-of-arrival) anchors additionally carry `orient` (a `{w,x,y,z}`
 * quaternion) and `rotMatrix` (a 3x3 number matrix), both optional and
 * both-or-neither. Until the API supports them they arrive as unrecognised
 * keys, which this schema already permits and which the upload keeps in the
 * posted payload. To enforce them, add the two fields below and a
 * `superRefine` for the both-or-neither rule.
 */
export const anchorUploadEntrySchema = z.object({
  id: anchorIdSchema,
  index: z.number().int().optional(),
  pos: pitchPointUploadEntrySchema,
  serial: z.string().optional()
})

/**
 * Pitch point keys are free-form labels on the API side (`map[string]`).
 *
 * A document must carry at least one entry. A record is otherwise satisfied
 * vacuously by `{}`, and the API accepts an empty document too — an empty
 * points document there falls back to the sport's default pitch — so nothing
 * downstream would tell an operator that the file they picked was truncated.
 */
export const pitchPointsUploadDocumentSchema = z
  .record(z.string(), pitchPointUploadEntrySchema)
  .refine(
    (document) => Object.keys(document).length > 0,
    'must contain at least one pitch point'
  )

/**
 * The anchor document's keys are Crockford codes on the API side, not plain
 * strings (`map[crockford.CrockfordCode]`), so a key carrying I, L, O, U, a
 * space, or too many digits fails to decode there. Validating keys here turns
 * that 400 into a named error against the offending key.
 *
 * A document must carry at least one entry, for the same reason as the pitch
 * points above: the API unmarshals `{}` into an empty anchor map without
 * complaint, so a truncated export would otherwise post anchor geometry with
 * no anchors in it.
 */
export const anchorUploadDocumentSchema = z
  .record(crockfordCodeSchema, anchorUploadEntrySchema)
  .refine(
    (document) => Object.keys(document).length > 0,
    'must contain at least one anchor'
  )

export type PitchPointUploadEntry = z.infer<typeof pitchPointUploadEntrySchema>
export type AnchorUploadEntry = z.infer<typeof anchorUploadEntrySchema>
export type PitchPointsUploadDocument = z.infer<
  typeof pitchPointsUploadDocumentSchema
>
export type AnchorUploadDocument = z.infer<typeof anchorUploadDocumentSchema>
